import { loadWrapper } from "aic";
import { useAppSettings, useHierarchyContext } from "contexts/AppSettingsContext";
import { eHierarchyLevel } from "functions/api/enums";
import { HierarchyQueryParams } from "functions/api/hierarchy/HierarchyQueryParams";
import { isHierarchyReadDto } from "functions/api/hierarchy/HierarchyReadDto";
import { loadHierarchy } from "functions/api/hierarchy/loadHierarchy";
import { IRequestStatus } from "functions/api/interface";
import { getMyAccess } from "functions/api/user/getMyAccess";
import { useCallback, useContext, useEffect, useEffectEvent, useReducer, useState } from "react";
import { HierarchyBrowserContext } from "./HierarchyBrowserContext";
import { hierarchyBrowserReducer } from "./hierarchyBrowserReducer";
import { hierarchyToNodes } from "./hierarchyToNodes";
import {
  ADD_HIERARCHY_NODES,
  IHierarchyBrowserContext,
  SET_CORPORATE_HIERARCHY,
  SET_CURRENT_ACCESS_RIGHTS,
} from "./interface";

interface HierarchyBrowserContextProviderProps {
  initialState?: Partial<IHierarchyBrowserContext>;
  children: React.ReactNode;
}

export const defaultHierarchyBrowserContext: IHierarchyBrowserContext = {
  id: "hierarchy-browser",
  root: "",
  treeList: [],
  currentHierarchy: "",
  currentHierarchyType: null,
  currentAccessRights: [],
  showTableLinks: false,
  canSelect: false,
  selections: [],
  hierarchyTlfItemIndex: [],
};
/**
 * Provides a hierarchy browser context to child components.
 *
 * @param props - Provider props
 * @param props.initialState - Optional initial state to seed the provider
 * @param props.children - React children to render inside the provider
 */
export const HierarchyBrowserContextProvider = ({
  initialState,
  children,
}: HierarchyBrowserContextProviderProps) => {
  const appSettings = useAppSettings();
  const hierarchyContext = useHierarchyContext();

  const [state, dispatch] = useReducer(hierarchyBrowserReducer, {
    id: initialState?.id ?? defaultHierarchyBrowserContext.id,
    root:
      initialState?.root ??
      hierarchyContext?.hierarchyNodes.find((h) => h.level === eHierarchyLevel.Corporate)?.id ??
      defaultHierarchyBrowserContext.root,
    treeList:
      initialState?.treeList ??
      (hierarchyContext !== null
        ? hierarchyToNodes(hierarchyContext.hierarchyNodes)
        : // istanbul ignore next
          defaultHierarchyBrowserContext.treeList),
    currentHierarchy:
      initialState?.currentHierarchy ??
      hierarchyContext?.hierarchy?.id ??
      defaultHierarchyBrowserContext.currentHierarchy,
    currentHierarchyType:
      hierarchyContext?.hierarchyNodes.find(
        (h) =>
          h.id ===
          (initialState?.currentHierarchy ??
            hierarchyContext?.hierarchy?.id ??
            // istanbul ignore next
            defaultHierarchyBrowserContext.currentHierarchy),
      )?.type ?? defaultHierarchyBrowserContext.currentHierarchyType,
    currentAccessRights: defaultHierarchyBrowserContext.currentAccessRights,
    showTableLinks: initialState?.showTableLinks ?? defaultHierarchyBrowserContext.showTableLinks,
    canSelect: initialState?.canSelect ?? defaultHierarchyBrowserContext.canSelect,
    selections: initialState?.selections ?? defaultHierarchyBrowserContext.selections,
    hierarchyTlfItemIndex:
      initialState?.hierarchyTlfItemIndex ?? defaultHierarchyBrowserContext.hierarchyTlfItemIndex,
  });

  const [corpRequest, setCorpRequest] = useState<IRequestStatus<HierarchyQueryParams>>({
    requesting: false,
    error: false,
  });
  const getCorporate = useEffectEvent(async () => {
    const request = await loadWrapper(
      loadHierarchy,
      { hierarchyName: "Corporate" },
      corpRequest,
      setCorpRequest,
      appSettings.backEndUrl,
    );
    /* istanbul ignore else */
    if (request?.success) {
      dispatch({
        operation: SET_CORPORATE_HIERARCHY,
        hierarchyNode: request.data,
      });
    }
  });

  useEffect(() => {
    if (!hierarchyContext?.hierarchy) {
      getCorporate();
    }
  }, [hierarchyContext?.hierarchy]);

  // Update current if an unknown item is selected
  const [loadCurrent, setLoadCurrent] = useState<IRequestStatus<HierarchyQueryParams>>({
    requesting: false,
    error: false,
  });
  const getHierarchyAsync = useCallback(
    async (hierarchyId: string) => {
      /* istanbul ignore else */
      if (hierarchyContext) {
        const response = await loadWrapper(
          loadHierarchy,
          { hierarchyId },
          { ...loadCurrent, error: false, errorText: undefined },
          setLoadCurrent,
          appSettings.backEndUrl,
        );
        if (response?.success) {
          dispatch({
            operation: ADD_HIERARCHY_NODES,
            treeListData: hierarchyToNodes([response.data]),
          });
          hierarchyContext.dispatch({
            operation: ADD_HIERARCHY_NODES,
            hierarchyNodes: [response.data],
          });
        }
      }
    },
    [appSettings.backEndUrl, hierarchyContext, loadCurrent],
  );
  useEffect(() => {
    const currentNode = state.treeList.find((n) => n.id === state.currentHierarchy);
    if (state.currentHierarchy && (!currentNode || !isHierarchyReadDto(currentNode.data))) {
      getHierarchyAsync(state.currentHierarchy);
    }
  }, [getHierarchyAsync, state]);

  // Get current access rights
  const [arRequest, setArRequest] = useState<IRequestStatus<string>>({
    requesting: false,
    error: false,
  });
  const updateAccessRights = useCallback(
    async (hierarchyId: string) => {
      if (hierarchyId !== "") {
        const request = await loadWrapper(
          getMyAccess,
          hierarchyId,
          arRequest,
          setArRequest,
          appSettings.backEndUrl,
        );
        if (request?.success) {
          dispatch({
            operation: SET_CURRENT_ACCESS_RIGHTS,
            accessRights: request.data,
          });
        } else if (request?.success === false) {
          dispatch({
            operation: SET_CURRENT_ACCESS_RIGHTS,
            accessRights: [],
          });
        }
      } else {
        dispatch({
          operation: SET_CURRENT_ACCESS_RIGHTS,
          accessRights: [],
        });
      }
    },
    [appSettings.backEndUrl, arRequest],
  );
  useEffect(() => {
    updateAccessRights(state.currentHierarchy);
  }, [state.currentHierarchy, updateAccessRights]);

  return (
    <HierarchyBrowserContext.Provider value={{ state, dispatch, arRequest, loadCurrent }}>
      {children}
    </HierarchyBrowserContext.Provider>
  );
};

HierarchyBrowserContextProvider.displayName = "HierarchyBrowserContextProvider";

/**
 * Hook to consume the hierarchy browser context.
 * Returns a context object or sensible defaults when context is not available.
 */
export const useHBC = () => {
  const context = useContext(HierarchyBrowserContext);
  return !context
    ? {
        state: null,
        dispatch: null,
        loadCurrent: { error: false, requesting: false },
        arRequest: { error: false, requesting: false },
      }
    : context;
};
